import { createSelector } from 'reselect'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import { getTravellerFormSchemaRequest, isTravellerFormNeeded } from 'checkout/selectors/request/travellerSchema'
import { isStandaloneGiftCard } from 'checkout/selectors/view/giftCard'
import { isStandalonePurchaseCredit } from 'checkout/selectors/view/purchaseCredit'
import { isStandaloneLoyaltyPurchasePoints } from 'checkout/selectors/view/loyaltyPurchasePoints'
import { getVillaItems } from './view/villa'
import { getBookingCabinPricingData, getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import getCheckoutTravellerSchema, { isCheckoutTravellerReadinessSatisfied } from 'checkout/selectors/getCheckoutTravellerSchema'
import getIsCheckoutTravellerSchemaFetching from './getCheckoutTravellerSchemaFetching'
import { allFormsCompleted } from 'lib/checkout/form'
import getPayableTotal from './payment/getPayableTotal'
import getIsLoyaltyFreeLoungePassBenefitPurchase from 'checkout/selectors/payment/loyalty/getIsLoyaltyFreeLoungePassBenefitPurchase'
import { getCurrentIntl } from 'i18n/util/currentIntl'

const getCheckoutLocale = (state: App.State) => state.geo.locale

export const shouldShowTravellerForm = createSelector(
  (state: App.State) => state.checkout.isCartRestored,
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (isCartRestored, isTravellerFormNeeded, formRequest): boolean => {
    return isCartRestored && isTravellerFormNeeded && formRequest.items.length > 0
  },
)

export const shouldShowArrivalDetailsForm = createSelector(
  (state: App.State) => getVillaItems(state),
  (villaItems): boolean => {
    return !!villaItems.length
  },
)

/**
 * @remarks
 * Edge case for cruise items in the checkout.
 * Due to a technical limitation we cannot correctly display deposit/payment schedule options
 * until 'cabin preferences' are selected
 */
export const shouldShowPaymentForm = createSelector(
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => isStandaloneGiftCard(state),
  (state: App.State) => isStandalonePurchaseCredit(state),
  (state: App.State) => isStandaloneLoyaltyPurchasePoints(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getBookingCabinPricingData(state),
  (state: App.State) => getPayableTotal(state) > 0,
  (state: App.State) => getIsLoyaltyFreeLoungePassBenefitPurchase(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getIsCheckoutTravellerSchemaFetching(state),
  (state: App.State) => state.checkout.isFormInitialised,
  (isTravellerFormNeeded, showForm, isGiftCard, isPurchaseCredit, isLoyaltyPurchasePoints, cruiseItems, cabinPricingData, hasPayableTotal, isLoyaltyFreeLoungePassBenefitPurchase, schemaData, isFetchingSchema, isFormInitialised): boolean => {
    const isTravellerFormReady = isCheckoutTravellerReadinessSatisfied({
      isTravellerFormNeeded,
      shouldShowTravellerForm: showForm,
      isStandaloneGiftCard: isGiftCard,
      isStandalonePurchaseCredit: isPurchaseCredit,
      isStandaloneLoyaltyPurchasePoints: isLoyaltyPurchasePoints,
      isLoyaltyFreeLoungePassBenefitPurchase,
      schemaData,
      isFetchingSchema,
      isFormInitialised,
    })

    if (cruiseItems.length) {
      return !!cabinPricingData.hasCabinPricing
    }

    const shouldShowPayment = !isTravellerFormNeeded || showForm || isGiftCard || isPurchaseCredit || hasPayableTotal || isLoyaltyFreeLoungePassBenefitPurchase || isLoyaltyPurchasePoints
    return shouldShowPayment && isTravellerFormReady
  },
)

export const hasUnpurchasableItems = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (experienceItemsView) => {
    return {
      hasRequiredData: experienceItemsView.hasRequiredData,
      data: experienceItemsView.hasRequiredData && experienceItemsView.data.some(item => (
        item.ticketViews.some(ticket => ticket.unavailable)
      )),
    }
  },
)

const getRequiredNumberOfForms = createSelector(
  (state: App.State) => getCheckoutTravellerSchema(state),
  (schemaData): number => schemaData?.accommodationFlightSchema ?
    Object.keys(schemaData.accommodationFlightSchema.properties).length :
    0,
)

export const getCheckoutSingleStep = createSelector(
  (state: App.State) => state.checkout.isCartRestored,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getIsCheckoutTravellerSchemaFetching(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => hasUnpurchasableItems(state),
  (state: App.State) => getRequiredNumberOfForms(state),
  getCheckoutLocale,
  (isCartRestored, schemaData, isFetchingSchema, travellerForms,
    isGift, shouldShowForm, hasUnpurchasableItems, requiredTravellerCount, _locale): App.Checkout.Step => {
    const intl = getCurrentIntl()
    const isFormReady = (isCartRestored && !isFetchingSchema && (schemaData || !shouldShowForm))
    const reviewGiftLabel = intl.formatMessage({
      id: 'TzVpZyqIlZpqwtOJojE2',
      defaultMessage: 'Review your gift',
      description: 'Single checkout step label when reviewing a gift purchase in checkout',
    })
    const reviewAndPayLabel = intl.formatMessage({
      id: 'gQEaHDKGcuGHj8cSea4N',
      defaultMessage: 'Review & Pay',
      description: 'Single checkout step label when reviewing and paying in checkout',
    })

    const result = !!schemaData?.accommodationFlightSchema &&
      travellerForms.length === requiredTravellerCount &&
      allFormsCompleted(schemaData.accommodationFlightSchema, travellerForms) &&
      !hasUnpurchasableItems.data

    return {
      id: 'purchase',
      name: isGift ? reviewGiftLabel : reviewAndPayLabel,
      shortName: 'purchase',
      isComplete: {
        hasRequiredData: isFormReady && hasUnpurchasableItems.hasRequiredData,
        data: result,
      },
    }
  },
)

// TODO: https://aussiecommerce.atlassian.net/browse/CRO-3597 Remove steps concept for new checkout as it's single step
export const getCheckoutSteps = createSelector(
  (state: App.State) => getCheckoutSingleStep(state),
  (checkoutSingleStep): Array<App.Checkout.Step> => {
    return [checkoutSingleStep]
  },
)
